<template>
	<div class="flat-select">
		<o-input
			v-if="showSearch"
			:ref="setup._search"
			v-model="setup.searchTerm"
			suffix-icon="search_magnifying_glass"
			:label="$t('common.shared.components.flatSelect.search')"/>

		<ul
			v-if="options.length || loading"
			class="flat-select__options">
			<li
				v-for="(item, index) in options"
				:key="item.id"
				class="flat-select__item"
				:class="{
					'flat-select__item--selected': setup.itemIsSelected(item),
					'flat-select__item--partial-selected': setup.itemIsPartialSelected(item),
				}"
				@click="setup.selectItemAsync(item)">
				<slot v-bind="{ item, index }"/>
				<div>
					<o-icon
						class="flat-select__item-check"
						icon="check"/>
					<span
						v-if="setup.itemIsPartialSelected(item)"
						class="flat-select__item-check-partial">
						&minus;
					</span>
					<o-icon
						v-if="setup.itemIsSelected(item)"
						class="flat-select__item-uncheck"
						icon="trash_empty"/>
				</div>
			</li>
		</ul>

		<div
			v-else
			class="flat-select__options">
			<slot name="empty"/>
		</div>

		<o-button
			v-if="!!newLabel"
			class="flat-select__new"
			block
			nude
			prefix-font-icon="icon-interface-add-circle"
			color="info"
			@click="emits('new')">
			{{ newLabel }}
		</o-button>

		<o-loader
			:visible="loading"
			size="md"
			:message="$t('common.shared.components.flatSelect.loading')"/>
	</div>
</template>

<script setup lang="ts">
// #region Setup
import type { SharedFlatSelectEmits, SharedFlatSelectProps } from './SharedFlatSelectSetup';
import SharedFlatSelectSetup from './SharedFlatSelectSetup';
const emits = defineEmits<SharedFlatSelectEmits>() as SharedFlatSelectEmits;
const props = withDefaults(defineProps<SharedFlatSelectProps>(), SharedFlatSelectSetup.defaultProps);
const setup = new SharedFlatSelectSetup(props, emits);
// #endregion
</script>

<style lang="less" scoped>
.flat-select {
	&__options {
		overflow: auto;
		min-width: 10rem;
		min-height: 2rem;
		max-height: max(30vh, 12.5rem);
		padding: 0;
	}

	&__item {
		cursor: pointer;
		display: flex;
		gap: 1.25rem;
		align-items: center;
		justify-content: space-between;
		padding: 0.5rem 0.75rem;
		font-size: calc(14rem / 16);

		:not(&--selected) {
			.flat-select__item-check {
				opacity: 0;
			}
		}

		&:first-of-type {
			border-radius: 0.25rem 0.25rem 0 0;
		}

		&:last-of-type {
			border-radius: 0 0 0.25rem 0.25rem;
		}

		&--partial-selected {
			font-weight: 600;
			background: var(--grey-light);

			.flat-select__item-check-partial {
				color: var(--grey-dark);
			}

			&:hover {
				.flat-select__item-check-partial {
					display: none;
				}
			}
		}

		&--selected {
			font-weight: 600;
			background-color: rgba(var(--rgb-info), 0.2);

			.orion-icon {
				color: var(--info);
				opacity: 1;
			}

			.flat-select__item-uncheck {
				display: none;
			}

			&:hover {
				background-color: rgba(var(--rgb-danger), 0.15);

				.orion-icon {
					color: var(--danger);
				}

				.flat-select__item-check {
					display: none;
				}

				.flat-select__item-uncheck {
					display: block;
				}
			}
		}

		&:hover:not(&--selected) {
			background: var(--grey-lighter);

			.flat-select__item-check {
				opacity: 1;
			}
		}
	}

	&__new {
		margin-top: var(--space-sm);
		background: var(--grey-lighter);
	}

	.orion-input {
		min-width: 14rem;
		margin: 0 0 1rem;
	}
}
</style>
